import ResponsiveWrapper from "../ResponsiveWrapper/ResponsiveWrapper";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar, Line } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { getIncomeSubCategoryColor } from "../../utils/categoryColors";
import { formatCurrency } from "../../utils/format";
import { getLocalizedMonthNameFromYearMonth } from "../../utils/date";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { useScreenHeight, getChartHeight } from "../../hooks/useScreenHeight";

const margin = { top: 20, right: 85, bottom: 50, left: 50 };

const StackedBarChart = ({ data, averages, translate }) => {
	const { locale } = useUserPreferences();
	const screenHeight = useScreenHeight();
	const chartHeight = getChartHeight(screenHeight);

	// Leave some space for the chart legend
	const legendHeight = 60;
	const actualChartHeight = chartHeight - legendHeight;

	// Tooltip setup
	const {
		tooltipData,
		tooltipLeft,
		tooltipTop,
		tooltipOpen,
		showTooltip,
		hideTooltip,
	} = useTooltip();
	const { containerRef, TooltipInPortal } = useTooltipInPortal({
		scroll: true,
	});

	const renderChart = ({ width, height }) => {
		if (width === 0 || height === 0) return null;

		const xMax = width - margin.left - margin.right;
		const yMax = height - margin.top - margin.bottom;

		// Create scale for months
		const xScale = scaleBand({
			domain: data.map(d => d.month),
			range: [0, xMax],
			padding: 0.2,
		});

		// Map of month ids to localized month names
		const monthLabels = {};
		data.forEach(d => {
			if (!monthLabels[d.month]) {
				monthLabels[d.month] = getLocalizedMonthNameFromYearMonth(
					d.month,
					locale
				);
			}
		});

		// Get all income subcategories
		const incomeSubCategories = [
			...new Set(data.flatMap(d => Object.keys(d.incomeSubCategories || {}))),
		];

		// Calculate income scales
		const maxIncome = Math.max(...data.map(d => d.totalIncome || 0));
		const avgIncome = typeof averages === "number" ? averages : 0;
		const maxValue = Math.max(maxIncome, avgIncome);

		const yScale = scaleLinear({
			domain: [0, maxValue * 1.1],
			nice: true,
			range: [yMax, 0],
		});

		return (
			<svg width={width} height={height}>
				<Group top={margin.top} left={margin.left}>
					{/* Render stacked bars for each month */}
					{data.map(monthData => {
						const monthX = xScale(monthData.month);
						if (monthX == null) return null;

						const monthLabel = monthLabels[monthData.month];

						// Income stacked bar
						let stackY = yScale(0);
						const incomeStack = incomeSubCategories.map(subCategory => {
							const value = monthData.incomeSubCategories?.[subCategory] || 0;
							if (value === 0) return null;

							const barHeight = yScale(0) - yScale(value);
							const currentY = stackY - barHeight;
							stackY = currentY;

							return (
								<Bar
									key={`income-${monthData.month}-${subCategory}`}
									x={monthX}
									y={currentY}
									width={xScale.bandwidth() * 0.9}
									height={barHeight}
									fill={getIncomeSubCategoryColor(subCategory)}
									rx={2}
									onMouseMove={e => {
										const event = e.nativeEvent;
										showTooltip({
											tooltipLeft: event.offsetX,
											tooltipTop: event.offsetY,
											tooltipData: {
												category: `${translate(
													`subCategory.income.${subCategory}`
												)}`,
												rawSubCategory: subCategory,
												label: monthLabel,
												value,
												type: "income",
											},
										});
									}}
									onMouseLeave={hideTooltip}
								/>
							);
						});

						return (
							<Group key={`month-${monthData.month}`}>{incomeStack}</Group>
						);
					})}

					{/* Average line */}
					{avgIncome > 0 && (
						<Group>
							<Line
								from={{ x: 0, y: yScale(avgIncome) }}
								to={{ x: xMax, y: yScale(avgIncome) }}
								stroke="#8B5CF6"
								strokeWidth={2}
								strokeDasharray="5,5"
								opacity={0.7}
							/>
							<text
								x={xMax + 5}
								y={yScale(avgIncome)}
								dy="0.3em"
								fontSize={10}
								fill="#8B5CF6"
								fontWeight="bold"
							>
								{translate("analysis.average")}:{" "}
								{formatCurrency(avgIncome, locale)}
							</text>
						</Group>
					)}

					{/* Y-axis */}
					<AxisLeft
						scale={yScale}
						stroke="var(--text-color)"
						tickLabelProps={() => ({
							fill: "var(--text-color)",
							fontSize: 11,
							textAnchor: "end",
						})}
						numTicks={6}
						tickLineProps={{
							stroke: "var(--text-color)",
							strokeWidth: 1,
						}}
					/>

					{/* X-axis */}
					<AxisBottom
						scale={xScale}
						top={yMax}
						stroke="var(--text-color)"
						tickFormat={d => monthLabels[d] || d}
						tickLabelProps={() => ({
							fill: "var(--text-color)",
							fontSize: 11,
							textAnchor: "middle",
							angle: -45,
							dx: "-0.25em",
							dy: "0.5em",
						})}
						tickLineProps={{
							stroke: "var(--text-color)",
							strokeWidth: 1,
						}}
					/>
				</Group>
			</svg>
		);
	};

	return (
		<div
			className="chart-box"
			style={{ height: `${chartHeight}px` }}
			ref={containerRef}
		>
			<ResponsiveWrapper fallbackHeight={actualChartHeight}>
				{renderChart}
			</ResponsiveWrapper>

			{/* Income subcategories legend */}
			<div className="stacked-legend">
				{data.length > 0 &&
					[
						...new Set(
							data.flatMap(d => Object.keys(d.incomeSubCategories || {}))
						),
					].map(subCategory => (
						<div className="legend-item" key={subCategory}>
							<div
								className="legend-color"
								style={{
									backgroundColor: getIncomeSubCategoryColor(subCategory),
								}}
							/>
							<span>{translate(`subCategory.income.${subCategory}`)}</span>
						</div>
					))}
			</div>

			{tooltipOpen && tooltipData && (
				<TooltipInPortal
					top={tooltipTop}
					left={tooltipLeft}
					className="chart-tooltip"
				>
					<div
						className="tooltip-header"
						style={{
							color: getIncomeSubCategoryColor(tooltipData.rawSubCategory),
						}}
					>
						{tooltipData.category}
					</div>
					<div className="tooltip-label">{tooltipData.label}</div>
					<div className="tooltip-value">
						{formatCurrency(tooltipData.value, locale)}
					</div>
				</TooltipInPortal>
			)}
		</div>
	);
};

export default StackedBarChart;
